// Verlauf eines Werts über ein gleitendes Fenster (Karten der Startseite:
// Geräte-Karte im Verlauf-Modus, „Lüftung & Heizung").
//
// Der erste Abruf holt das ganze Fenster, danach nur, was seit dem letzten
// Punkt dazukam (`abrufAb`); vorn fällt heraus, was älter als das Fenster ist
// -- der letzte herausgefallene Punkt wird der Startzustand (`prior`). Bis
// 03.11.2026 holten die Karten alle 2 min den ganzen 24-h-Verlauf jedes Werts
// eines Geräts (~300 kB je 3 min auf der Startseite).
//
// Der Server vergleicht Zeitstempel als Text (entity_history.ts), Bruchteile
// unterschiedlicher Länge sortieren dort nicht sicher. Darum überlappt der
// Folgeabruf eine Sekunde, und doppelte Punkte fallen hier über ihren
// Zeitstempel heraus.

export interface VerlaufPunkt {
  ts: string;
  num?: number | null;
}

export interface Fenster {
  /** letzter Punkt vor dem Fenster (Startzustand am linken Rand) */
  prior: VerlaufPunkt | null;
  /** Punkte im Fenster, aufsteigend */
  punkte: VerlaufPunkt[];
}

/** Antwort von GET /v2-api/iot/devices/{slug}/history, auf einen Wert gefiltert. */
export interface VerlaufAntwort {
  prior?: VerlaufPunkt | null;
  points?: VerlaufPunkt[];
}

const UEBERLAPPUNG_MS = 1000;

const ms = (p: VerlaufPunkt): number => Date.parse(p.ts);

/** Ab wann der nächste Abruf holen muss (ISO): ohne Punkte das ganze
 *  Fenster ab `von`, sonst ab dem letzten Punkt (mit Überlappung). */
export function abrufAb(f: Fenster | undefined, von: string): string {
  const letzter = f?.punkte.at(-1);
  if (!letzter) return von;
  const ab = ms(letzter) - UEBERLAPPUNG_MS;
  return Number.isFinite(ab) && ab > Date.parse(von) ? new Date(ab).toISOString() : von;
}

/** Das Fenster nach einem Abruf ab `ab`: war es das ganze Fenster
 *  (`ab === von`), gilt die Antwort samt `prior`; sonst kommen die neuen
 *  Punkte dazu. Danach fällt vorn heraus, was vor `von` liegt. */
export function fortschreiben(alt: Fenster | undefined, ab: string, von: string, antwort: VerlaufAntwort): Fenster {
  const neu = antwort.points ?? [];
  let prior: VerlaufPunkt | null;
  let punkte: VerlaufPunkt[];
  if (!alt || ab === von) {
    prior = antwort.prior ?? null;
    punkte = [...neu];
  } else {
    prior = alt.prior;
    const da = new Set(alt.punkte.map((p) => p.ts));
    const dazu = neu.filter((p) => !da.has(p.ts));
    punkte = dazu.length === 0 ? alt.punkte : [...alt.punkte, ...dazu].sort((a, b) => ms(a) - ms(b));
  }
  const grenze = Date.parse(von);
  let raus = 0;
  while (raus < punkte.length && ms(punkte[raus]) < grenze) raus++;
  if (raus > 0) {
    prior = punkte[raus - 1];
    punkte = punkte.slice(raus);
  }
  return { prior, punkte };
}
